import { createSlice } from '@reduxjs/toolkit';

// Helper function to calculate dominant color from base64 image
const calculateDominantColor = (base64Image) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      const colorCounts = new Map();

      // Sample pixels (every 4th pixel for performance)
      for (let i = 0; i < imageData.length; i += 16) {
        const r = imageData[i];
        const g = imageData[i + 1];
        const b = imageData[i + 2];

        // Skip transparent/white pixels
        if (r > 240 && g > 240 && b > 240) continue;

        // Round colors to reduce the number of unique colors
        const roundedR = Math.round(r / 10) * 10;
        const roundedG = Math.round(g / 10) * 10;
        const roundedB = Math.round(b / 10) * 10;

        const colorKey = `${roundedR},${roundedG},${roundedB}`;
        colorCounts.set(colorKey, (colorCounts.get(colorKey) || 0) + 1);
      }

      // Find the most frequent color
      let maxCount = 0;
      let dominantColor = 'rgb(128, 128, 128)'; // Default gray

      colorCounts.forEach((count, color) => {
        if (count > maxCount) {
          maxCount = count;
          const [r, g, b] = color.split(',').map(Number);
          dominantColor = `rgb(${r}, ${g}, ${b})`;
        }
      });

      resolve(dominantColor);
    };
    img.src = base64Image;
  });
};

const initialState = {
  favicons: {},
  loading: {},
  error: {},
  colors: {}
};

const faviconCacheSlice = createSlice({
  name: 'faviconCache',
  initialState,
  reducers: {
    setFavicon: (state, action) => {
      const { url, favicon } = action.payload;
      if (url && favicon) {
        state.favicons[url] = favicon;
        state.loading[url] = false;
        state.error[url] = null;
      }
    },
    setFaviconColor: (state, action) => {
      const { url, color } = action.payload;
      if (url && color) {
        state.colors[url] = color;
      }
    },
    setFaviconLoading: (state, action) => {
      const { url } = action.payload;
      if (url) {
        state.loading[url] = true;
        state.error[url] = null;
      }
    },
    setFaviconError: (state, action) => {
      const { url, error } = action.payload;
      if (url) {
        state.loading[url] = false;
        state.error[url] = error;
      }
    },
    clearFavicons: (state) => {
      state.favicons = {};
      state.loading = {};
      state.error = {};
      state.colors = {};
    }
  }
});

export const { setFavicon, setFaviconColor, setFaviconLoading, setFaviconError, clearFavicons } = faviconCacheSlice.actions;

// Thunk to handle favicon setting with color calculation
export const setFaviconWithColor = (url, favicon) => async (dispatch) => {
  dispatch(setFavicon({ url, favicon }));
  if (favicon) {
    try {
      const color = await calculateDominantColor(favicon);
      dispatch(setFaviconColor({ url, color }));
    } catch (error) {
      console.error('Error calculating favicon color:', error);
    }
  }
};

export default faviconCacheSlice.reducer;
